import type { KeyChain } from './keyChains'
import { localStorageOrNull } from './storage'

export type CreatedByFilter = 'anyone' | 'me'

export interface FilterOption {
  value: CreatedByFilter
  label: string
}

export const FILTER_OPTIONS: FilterOption[] = [
  { value: 'anyone', label: 'Anyone' },
  { value: 'me', label: 'Me' },
]

const STORAGE_KEY = 'apiKeys.createdBy'

/** "Me" compares ids without case and matches nothing without a signed-in user. */
export function matchesFilter(
  chain: KeyChain,
  filter: CreatedByFilter,
  currentUserId: string | null | undefined,
): boolean {
  if (filter === 'anyone') return true
  const creator = chain.createdBy
  return !!creator && !!currentUserId && creator.id.toLowerCase() === currentUserId.toLowerCase()
}

/** Anything but a remembered "me" reads as Anyone. */
export function readCreatedByFilter(storage: Storage | null = localStorageOrNull()): CreatedByFilter {
  try {
    return storage?.getItem(STORAGE_KEY) === 'me' ? 'me' : 'anyone'
  } catch {
    return 'anyone'
  }
}

export function writeCreatedByFilter(
  filter: CreatedByFilter,
  storage: Storage | null = localStorageOrNull(),
): void {
  try {
    storage?.setItem(STORAGE_KEY, filter)
  } catch {
    // Blocked storage: the choice lasts until the page reloads.
  }
}

/** What an empty "Me" says; it must not claim keys that have no recorded creator belong to others. */
export function emptyMeText(chains: KeyChain[]): string {
  const unrecorded = chains.filter((c) => !c.createdBy).length
  if (unrecorded === 0) return "You haven't created any of your company's keys."
  return (
    "None of your company's keys is recorded as yours. " +
    `${unrecorded} ${unrecorded === 1 ? 'key has' : 'keys have'} no recorded creator.`
  )
}
